/**
 * Password policy, defined once.
 *
 * The API enforces this and the UI displays it as a live checklist. Keeping one
 * definition means the list someone is ticking off cannot drift from the list
 * the server actually applies — a mismatch there reads as the app being broken.
 *
 * The server is still the enforcer. Anything here is a hint until it has been
 * checked again on the way in.
 */

export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 256;

export interface PasswordRule {
  readonly id: string;
  readonly label: string;
  readonly test: (password: string) => boolean;
}

/**
 * Length, case, and a digit. Symbols are allowed but deliberately not required:
 * mandatory symbols are what produce P@ssw0rd!, which is weaker than the same
 * number of ordinary letters. The length floor does most of the real work.
 */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    test: (password) => password.length >= MIN_PASSWORD_LENGTH,
  },
  {
    id: 'lower',
    label: 'A lower-case letter',
    test: (password) => /[a-z]/.test(password),
  },
  {
    id: 'upper',
    label: 'An upper-case letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'digit',
    label: 'A number',
    test: (password) => /\d/.test(password),
  },
];

/** Passwords weak enough to be worth naming, whatever their shape. */
const OBVIOUS_PASSWORDS = new Set([
  'password1234',
  'password12345',
  'passw0rd1234',
  'administrator1',
  'amazonseller1',
  'amazonseller12',
  'qwertyuiop123',
  'abcdefghijk1',
]);

export interface PasswordCheck {
  ok: boolean;
  /** Rules not yet satisfied, in declaration order. */
  failed: readonly PasswordRule[];
  reason?: string;
}

export function checkPassword(password: string): PasswordCheck {
  if (password.length > MAX_PASSWORD_LENGTH) {
    // Bounded so a huge input cannot be used to burn CPU in the hasher.
    return {
      ok: false,
      failed: [],
      reason: `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer.`,
    };
  }

  const failed = PASSWORD_RULES.filter((rule) => !rule.test(password));

  if (failed.length > 0) {
    return {
      ok: false,
      failed,
      reason: `Password needs: ${failed.map((rule) => rule.label.toLowerCase()).join(', ')}.`,
    };
  }

  if (OBVIOUS_PASSWORDS.has(password.toLowerCase())) {
    return {
      ok: false,
      failed: [],
      reason: 'That password is too easily guessed. Choose another.',
    };
  }

  // Aa1aaaaaaaaa satisfies every class rule and is still terrible.
  const lettersOnly = password.replace(/[^a-z]/gi, '');
  if (lettersOnly.length > 0 && /^(.)\1+$/i.test(lettersOnly)) {
    return { ok: false, failed: [], reason: 'Password cannot be a single repeated letter.' };
  }

  return { ok: true, failed: [] };
}
